import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldAlert, Info, Settings, LogIn } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

interface SellerAlertProps {
  isOpen: boolean;
  isLoggedIn: boolean; // 🟢 Noul prop adăugat
  onClose: () => void;
  onConfirm: () => void;
}

const SellerAlert = ({ isOpen, isLoggedIn, onClose, onConfirm }: SellerAlertProps) => {
  const { currentLanguageDictionary } = useAuth();
  const getDictionaryValue = (key: string): string => {
    if (!currentLanguageDictionary) return "";
    const item = currentLanguageDictionary.find((d: any) => d.dictKey === key);
    return item?.dictValue || "";
  };

  // Selectarea textelor în funcție de starea de autentificare (isLoggedIn)
  const headerText = isLoggedIn
    ? getDictionaryValue("alert.seller.config.header")
    : getDictionaryValue("alert.seller.header");

  const titleText = isLoggedIn
    ? getDictionaryValue("alert.seller.config.title")
    : getDictionaryValue("alert.seller.title");

  const descriptionText = isLoggedIn
    ? getDictionaryValue("alert.seller.config.description")
    : getDictionaryValue("alert.seller.description");

  const item1Text = isLoggedIn
    ? getDictionaryValue("alert.seller.config.item1")
    : getDictionaryValue("alert.seller.item1");

  const item2Text = isLoggedIn
    ? getDictionaryValue("alert.seller.config.item2")
    : getDictionaryValue("alert.seller.item2");

  const confirmButtonText = isLoggedIn
    ? getDictionaryValue("alert.seller.config.button.confirm")
    : getDictionaryValue("alert.seller.button.login");

  const cancelButtonText = getDictionaryValue("alert.seller.button.later");

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-[6px] p-6">
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="relative w-full max-w-md overflow-hidden rounded-[2.5rem] bg-[#0F172A] border border-orange-500/30 shadow-[0_0_50px_rgba(249,115,22,0.15)]"
          >
            {/* Header Decorativ */}
            <div className="h-2 bg-gradient-to-r from-orange-600 via-yellow-500 to-orange-600" />

            <div className="p-10">
              <div className="flex items-center gap-5 mb-8">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-500/10 text-orange-500 border border-orange-500/20 shadow-[0_0_20px_rgba(249,115,22,0.2)]">
                  <ShieldAlert size={36} />
                </div>
                <div>
                  <p className="text-[10px] font-black tracking-[0.3em] text-orange-500 uppercase">
                    {headerText}
                  </p>
                  <h3 className="text-2xl font-black text-white italic leading-tight">
                    {titleText}
                  </h3>
                </div>
              </div>

              <div className="space-y-5 mb-10">
                <p className="text-slate-300 text-sm leading-relaxed">
                  {descriptionText}
                </p>

                <div className="rounded-2xl bg-orange-500/5 border border-orange-500/10 p-5">
                  <ul className="space-y-4">
                    <li className="flex gap-4 text-xs text-slate-400">
                      <div className="mt-0.5 shrink-0">
                        <Info size={14} className="text-orange-500" />
                      </div>
                      <span className="leading-tight">
                        {item1Text}
                      </span>
                    </li>
                    <li className="flex gap-4 text-xs text-slate-400">
                      <div className="mt-0.5 shrink-0">
                        <Info size={14} className="text-orange-500" />
                      </div>
                      <span className="leading-tight">
                        {item2Text}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <button
                  onClick={onConfirm}
                  className="w-full px-6 py-4 rounded-xl bg-orange-600 text-white text-xs font-black uppercase tracking-widest hover:bg-orange-500 transition-all shadow-[0_0_20px_rgba(249,115,22,0.3)] flex items-center justify-center gap-3 group"
                >
                  {isLoggedIn ? (
                    <Settings size={16} className="group-hover:rotate-90 transition-transform duration-500" />
                  ) : (
                    <LogIn size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
                  )}
                  {confirmButtonText}
                </button>
                <button
                  onClick={onClose}
                  className="w-full px-6 py-3 rounded-xl text-slate-500 text-[10px] font-black uppercase tracking-widest hover:text-slate-300 transition-colors"
                >
                  {cancelButtonText}
                </button>
              </div>
            </div>

            {/* Fundal decorativ */}
            <div className="absolute -bottom-16 -right-16 opacity-[0.02] pointer-events-none text-white">
                <ShieldAlert size={240} />
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default SellerAlert;